import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useCart } from '@contexts/ShopCartContext';
import { makeStyles } from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { storeService } from '@services/storeService';
import { ProductCard } from '@components/shop/shared';
import JoinAlyzePlus from '@/assets/JoinAlyzePlus.png';
import theme from '@/theme/theme';

const useStyles = makeStyles({
    overlay: {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1001,
    },

    sidebar: {
        position: 'fixed',
        top: '0',
        right: '0',
        height: '100vh',
        width: '320px',
        backgroundColor: '#f5f0e6',
        transform: 'translateX(100%)',
        transition: 'transform 0.3s ease',
        zIndex: 1002,
        padding: '24px 0 0 0',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0px 0px 2px rgba(0, 0, 0, 0.12)',
        overflowY: 'auto',
        '@media (max-width: 520px)': {
            width: '100vw',
        },
    },

    sidebarOpen: {
        transform: 'translateX(0)',
    },

    sidebarHeader: {
        alignSelf: 'stretch',
        paddingRight: '24px',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '32px',
    },

    loginButton: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '6px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        textDecoration: 'none',
    },

    loginText: {
        color: theme.brandColors["80"], // #986F0B
        fontSize: '16px',
        fontFamily: 'Figtree',
        fontWeight: '600',
        lineHeight: '24px',
    },

    closeButtonSidebar: {
        width: '24px',
        height: '24px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.colorNeutralForeground2,
        padding: '0px',
    },

    sidebarContent: {
        alignSelf: 'stretch',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
    },

    sectionTitle: {
        color: theme.colorNeutralForeground4, // #707070
        fontSize: '16px',
        fontFamily: 'Figtree',
        fontWeight: '600',
        textTransform: 'uppercase',
        lineHeight: '20px',
    },

    categoryList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },

    categoryLink: {
        color: theme.colorNeutralForeground1, // #242424
        fontSize: '20px',
        fontFamily: 'Figtree',
        fontWeight: '500',
        lineHeight: '30px',
        textDecoration: 'none',
        '&:hover': {
            color: theme.brandColors["80"],
        },
    },

    bestSellersSection: {
        alignSelf: 'stretch',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '21px',
    },

    bestSellersHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },

    bestSellersNavigation: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
    },

    navArrow: {
        padding: '0px',
        width: '24px',
        height: '24px',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '4px',
        color: theme.colorNeutralForeground2,
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
        },
        '&:disabled': {
            opacity: 0.3,
            cursor: 'not-allowed',
        },
    },

    bestSellersCarousel: {
        position: 'relative',
        overflow: 'hidden',
    },

    bestSellersGrid: {
        display: 'flex',
        gap: '16px',
        transition: 'transform 0.3s ease',
        paddingBottom: '8px',
    },

    bestSellerCard: {
        minWidth: '200px',
        maxWidth: '200px',
        flexShrink: 0,
    },

    membershipSection: {
        alignSelf: 'stretch',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '21px',
    },

    membershipImage: {
        width: '100%',
        maxWidth: '432px',
        height: 'auto',
        aspectRatio: '432/519',
        objectFit: 'cover',
        borderRadius: '8px',
    },

    logoutSection: {
        alignSelf: 'stretch',
        padding: '24px',
        borderTop: `1px solid ${theme.colorNeutralStroke2}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        paddingBottom: '48px'
    },

    logoutButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        textDecoration: 'none',
        padding: '0',
    },

    logoutText: {
        color: theme.colorNeutralForeground1,
        fontSize: '20px',
        fontFamily: 'Figtree',
        fontWeight: '500',
        lineHeight: '30px',
    },

    cartButton: {
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '0',
        position: 'relative',
        '&:hover': {
            opacity: '0.8',
        },
    },

    cartIcon: {
        width: '24.10px',
        height: '24.08px',
        fill: theme.colorNeutralForeground2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
    },

    cartCount: {
        position: 'absolute',
        top: '-8px',
        right: '-8px',
        backgroundColor: '#D72B00',
        color: 'white',
        borderRadius: '50%',
        width: '20px',
        height: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: 'bold',
    },
});

interface StoreNavSidebarProps {
    isOpen: boolean;
    onClose: () => void;
}

export const StoreNavSidebar: React.FC<StoreNavSidebarProps> = ({ isOpen, onClose }) => {
    const styles = useStyles();
    const navigate = useNavigate();
    const auth = useAuth();
    const { itemCount, totalAmount, addItem } = useCart();
    
    const [categories, setCategories] = useState<string[]>([]);
    const [bestSellerProducts, setBestSellerProducts] = useState<any[]>([]);
    const [currentProductIndex, setCurrentProductIndex] = useState(0);

    // Fetch categories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const categoryList = await storeService.getProductCategories();
                setCategories(categoryList);
            } catch (error) {
                console.error('Error fetching categories:', error);
                // Fallback to hardcoded categories if API fails
                setCategories([
                    'Metabolism & Weight Loss',
                    'Energy & Performance',
                    'Hormonal & Sexual Health',
                    'Everyday Health',
                    'Anti-Aging & Vitality',
                    'Digestive & Gut Health',
                    'Inflammation & Immunity',
                    'Mind & Focus',
                    'Sleep & Calm',
                    'Home Detox'
                ]);
            }
        };

        fetchCategories();
    }, []);

    // Fetch best seller products
    useEffect(() => {
        const fetchBestSellerProducts = async () => {
            try {
                const products = await storeService.getBestSellerProducts();
                setBestSellerProducts(products.slice(0, 3)); // Take only first 3 products
            } catch (error) {
                console.error('Error fetching best seller products:', error);
            }
        };

        if (isOpen) {
            fetchBestSellerProducts();
        }
    }, [isOpen]);

    const handleLogin = () => {
        try {
            // Store current path for redirect after login
            const currentPath = window.location.pathname + window.location.search + window.location.hash;
            sessionStorage.setItem('auth.redirectPath', currentPath);
            auth.signinRedirect().catch((error) => {
                console.error("Login redirect failed:", error);
            });
        } catch (error) {
            console.error("Login handler error:", error);
            // Fallback to direct navigation
            window.location.href = '/auth/callback';
        }
    };

    const handleLogout = () => {
        const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;
        const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

        // Clear local session data
        sessionStorage.clear();
        localStorage.clear();

        // Redirect to Cognito logout URL with shop page as the logout URI
        const shopUrl = `${window.location.origin}/shop`;
        const logoutUrl = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(shopUrl)}`;
        window.location.href = logoutUrl;
    };

    const handlePreviousProduct = () => {
        if (bestSellerProducts.length > 0) {
            setCurrentProductIndex((prev) =>
                prev === 0 ? bestSellerProducts.length - 1 : prev - 1
            );
        }
    };

    const handleNextProduct = () => {
        if (bestSellerProducts.length > 0) {
            setCurrentProductIndex((prev) =>
                prev === bestSellerProducts.length - 1 ? 0 : prev + 1
            );
        }
    };

    const handleAddToCart = (product: any) => {
        addItem(product, 1);
    };

    const handleProductClick = () => {
        onClose();
    };

    const formatCategoryForUrl = (categoryName: string) => {
        // First decode HTML entities, then format for URL slug
        const tempElement = document.createElement('div');
        tempElement.innerHTML = categoryName;
        const decodedName = tempElement.textContent || tempElement.innerText || categoryName;
        // Convert to lowercase, replace spaces and & with dashes, remove special characters
        return decodedName.toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/&/g, '')
            .replace(/[^a-z0-9-]/g, '')
            .replace(/--+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    const decodeHtmlEntities = (text: string) => {
        const tempElement = document.createElement('div');
        tempElement.innerHTML = text;
        return tempElement.textContent || tempElement.innerText || text;
    };

    const handleCartClick = () => {
        onClose();
        navigate('/v1/shop/cart');
    };

    return (
        <>
            {isOpen && (
                <>
                    <div className={styles.overlay} onClick={onClose} />
                    <div className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : ''}`}>
                        {/* Header */}
                        <div className={styles.sidebarHeader}>
                            {!auth.isAuthenticated && (
                                <button onClick={handleLogin} className={styles.loginButton}>
                                    <span className={styles.loginText}>Log in</span>
                                </button>
                            )}
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <button
                                    onClick={handleCartClick}
                                    className={styles.cartButton}
                                    style={{ color: theme.colorNeutralForeground2 }}
                                >
                                    <span>${totalAmount.toFixed(2)}</span>
                                    <svg className={styles.cartIcon} viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M323 292L323 207C329 95 391 33 501 33 610 33 673 95 677 209V292H750C796 292 833 329 833 375V875C833 921 796 958 750 958H250C204 958 167 921 167 875V375C167 329 204 292 250 292H323ZM385 292H615L615 210C611 130 577 95 501 95 425 95 390 130 385 209L385 292ZM323 354H250C238 354 229 363 229 375V875C229 887 238 896 250 896H750C762 896 771 887 771 875V375C771 363 762 354 750 354H677V454C677 471 663 485 646 485S615 471 615 454L615 354H385L385 454C385 471 371 485 354 485 337 485 323 471 323 454L323 354Z" />
                                    </svg>
                                    {itemCount > 0 && (
                                        <span className={styles.cartCount}>{itemCount}</span>
                                    )}
                                </button>
                            </div>
                            <button onClick={onClose} className={styles.closeButtonSidebar}>
                                <Dismiss24Regular />
                            </button>
                        </div>

                        {/* Explore Section */}
                        <div className={styles.sidebarContent}>
                            <div className={styles.sectionTitle}>Explore</div>
                            <div className={styles.categoryList}>
                                {categories.map((category) => (
                                    <Link
                                        key={category}
                                        to={`/v1/product-category/${formatCategoryForUrl(category)}`}
                                        className={styles.categoryLink}
                                        onClick={onClose}
                                    >
                                        {decodeHtmlEntities(category)}
                                    </Link>
                                ))}
                            </div>
                        </div>

                        {/* Best Sellers Section */}
                        <div className={styles.bestSellersSection}>
                            <div className={styles.bestSellersHeader}>
                                <div className={styles.sectionTitle}>Best sellers</div>
                                {bestSellerProducts.length > 1 && (
                                    <div className={styles.bestSellersNavigation}>
                                        <button
                                            onClick={handlePreviousProduct}
                                            className={styles.navArrow}
                                            disabled={bestSellerProducts.length <= 1}
                                            aria-label="Previous product"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={handleNextProduct}
                                            className={styles.navArrow}
                                            disabled={bestSellerProducts.length <= 1}
                                            aria-label="Next product"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className={styles.bestSellersCarousel}>
                                <div
                                    className={styles.bestSellersGrid}
                                    style={{
                                        transform: `translateX(-${currentProductIndex * 216}px)` // 200px width + 16px gap
                                    }}
                                >
                                    {bestSellerProducts.map((product, index) => (
                                        <div key={product.productId || index} className={styles.bestSellerCard}>
                                            <ProductCard
                                                product={product}
                                                onAddToCart={handleAddToCart}
                                                hasMembership={auth.isAuthenticated}
                                                onClick={handleProductClick}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Membership Section */}
                        <div className={styles.membershipSection}>
                            <div className={styles.sectionTitle}>Membership</div>
                            <Link to="/v1/memberships" onClick={onClose}>
                                <img
                                    src={JoinAlyzePlus}
                                    alt="Join ALYZE+ - Know yourself like never before"
                                    className={styles.membershipImage}
                                />
                            </Link>                           
                        </div>

                        {/* Logout Section - only show if authenticated */}
                        {auth.isAuthenticated && (
                            <div className={styles.logoutSection}>
                                <button onClick={handleLogout} className={styles.logoutButton}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M5 21Q4.175 21 3.587 20.413Q3 19.825 3 19V5Q3 4.175 3.587 3.587Q4.175 3 5 3H12V5H5V19H12V21H5ZM16 17L14.6 15.55L17.15 13H9V11H17.15L14.6 8.45L16 7L21 12L16 17Z" fill={theme.colorNeutralForeground1} />
                                    </svg>
                                    <span className={styles.logoutText}>Logout</span>
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </>
    );
};
